import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import homeCareImage from "@/assets/recent-projects/homecare.webp.asset.json";
import resumeForgeImage from "@/assets/recent-projects/resumeforge.webp.asset.json";
import sourceSightImage from "@/assets/recent-projects/sourcesight.webp.asset.json";
import vitalisImage from "@/assets/recent-projects/vitalis.webp.asset.json";
import voxBenchImage from "@/assets/recent-projects/voxbench.webp.asset.json";

const PAGE_TITLE = "Recent Software Projects — Karnav Pargi";
const PAGE_DESCRIPTION =
  "Explore recent products I built across AI model evaluation, healthcare growth operations, and citation-first financial research.";

const projects = [
  {
    name: "VoxBench",
    category: "AI developer tool",
    url: "https://voxbench.karnavpargi.com/",
    domain: "voxbench.karnavpargi.com",
    image: voxBenchImage.url,
    imageAlt: "VoxBench interface for comparing voice-to-text AI models",
    summary:
      "I built VoxBench to replace guesswork when choosing a speech-to-text model. A team can record one answer, run the same audio through every available model, and compare the results under identical conditions.",
    outcome:
      "It turns a fragmented model-selection exercise into one repeatable benchmark, helping engineering teams weigh transcript quality against cost and response time before committing to a provider.",
    capabilities: [
      "Side-by-side OpenAI and Google model evaluation",
      "Per-model transcript, rating, token, cost, and latency results",
      "Bring-your-own-key flow with credentials kept in the browser",
      "Past-run history for consistent model comparisons",
    ],
    tags: ["Next.js", "TypeScript", "OpenAI", "Google AI", "Web Audio"],
  },
  {
    name: "Vitalis CRM",
    category: "Healthcare growth platform",
    url: "https://vitalsync.karnavpargi.com/",
    domain: "vitalsync.karnavpargi.com",
    image: vitalisImage.url,
    imageAlt: "Vitalis CRM healthcare growth dashboard",
    summary:
      "I designed Vitalis CRM as one operating view for hospitals, clinics, and diagnostic centres. It connects the journey from a new enquiry to a booked appointment and ultimately to attributable revenue.",
    outcome:
      "Growth and operations teams can see where patients come from, which referrals convert, where appointments drop off, and how each service line performs without reconciling separate tools.",
    capabilities: [
      "Visual healthcare lead and appointment conversion pipelines",
      "Inbound and outbound physician referral tracking",
      "Patient acquisition cost, channel, and lifetime-value analysis",
      "Campaign, doctor relationship, payer mix, and revenue reporting",
    ],
    tags: ["React", "TypeScript", "Multi-tenant SaaS", "Healthcare CRM", "Analytics"],
  },
  {
    name: "SourceSight",
    category: "RAG research copilot",
    url: "https://sourcesight.kpargi.eu.org/",
    domain: "sourcesight.kpargi.eu.org",
    image: sourceSightImage.url,
    imageAlt: "SourceSight secure research copilot sign-in screen",
    summary:
      "I built SourceSight for equity-research analysts who lose hours searching long regulatory filings. They can ask a plain-English question across a curated corpus of 10-K and 10-Q documents and receive an answer grounded in the source material.",
    outcome:
      "Citation-first responses let analysts move from document intake to original analysis faster while preserving the evidence trail needed to verify every material claim.",
    capabilities: [
      "Hybrid semantic and full-text retrieval across SEC filings",
      "Answers linked to citable passages in indexed documents",
      "Secure email-only access and conversation history",
      "Document ingestion, chunking, embeddings, and grounded generation",
    ],
    tags: ["FastAPI", "React", "TypeScript", "PostgreSQL", "pgvector", "OpenAI", "RAG"],
  },
  {
    name: "Aria Care",
    category: "Elder care platform",
    url: "https://homecare.karnavpargi.com/",
    domain: "homecare.karnavpargi.com",
    image: homeCareImage.url,
    imageAlt: "Aria Care elder care dashboard with vitals, medication reminder, and emergency button",
    summary:
      "I built Aria Care so seniors, their families, doctors, and caregivers all work from one calm, accessible view. The senior screen uses large buttons and voice control, while everyone else sees the same live picture of how the day is going.",
    outcome:
      "Families get reassurance without phoning constantly, and care teams spot problems early instead of hearing about them days later.",
    capabilities: [
      "Voice-led daily wellness check-ins for mood, pain, and sleep",
      "Medication reminders, fall detection, and one-tap emergency alerts",
      "Heart rate, SpO2, blood pressure, and step monitoring with trends",
      "Separate senior, family, doctor, and caregiver views with GPS safe zones",
    ],
    tags: ["React", "TypeScript", "Accessibility", "Web Speech API", "Realtime alerts"],
  },
  {
    name: "ResumeForge",
    category: "AI career tool",
    url: "https://resumeforge.karnavpargi.com/",
    domain: "resumeforge.karnavpargi.com",
    image: resumeForgeImage.url,
    imageAlt: "ResumeForge resume analysis screen showing an ATS score and AI suggestions",
    summary:
      "I built ResumeForge to remove the guesswork from job applications. Upload a resume, get a score against the applicant tracking systems recruiters actually use, paste a job description, and see exactly which lines to rewrite.",
    outcome:
      "Candidates stop rewriting blindly and recruiting teams get a single workspace to score, shortlist, and annotate inbound resumes.",
    capabilities: [
      "ATS scoring modelled on Lever, Greenhouse, and Workday parsers",
      "Job-description matching with gap analysis and a rewrite plan",
      "Role-specific cover letter generation in the candidate's voice",
      "Version history with score comparison and a recruiter dashboard",
    ],
    tags: ["React", "TypeScript", "AI analysis", "ATS parsing", "SaaS"],
  },
] as const;

export const Route = createFileRoute("/projects/")({
  component: RecentProjectsPage,
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://karnavpargi.com/projects" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://karnavpargi.com/projects" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
          url: "https://karnavpargi.com/projects",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: projects.map((project, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "SoftwareApplication",
                name: project.name,
                applicationCategory: project.category,
                url: project.url,
                description: project.summary,
              },
            })),
          },
        }),
      },
    ],
  }),
});

function RecentProjectsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" aria-label="Karnav Pargi Home" className="font-display text-xl font-bold tracking-tight">
            Karnav<span className="text-primary">.</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card/50 hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Back to site
          </Link>
        </nav>
      </header>

      <main>
        <section className="border-b border-border/50">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-24">
            <p className="mb-5 font-mono text-xs uppercase text-primary">Recently shipped</p>
            <h1 className="max-w-4xl text-5xl font-bold leading-tight md:text-7xl">Products built around real workflows.</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Three working products where I translated a focused business problem into clear architecture, usable software, and a measurable operational advantage.
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-6">
          {projects.map((project, index) => (
            <article key={project.name} className="grid gap-10 border-b border-border/50 py-16 last:border-0 md:py-24 lg:grid-cols-12 lg:gap-16">
              <div className={`lg:col-span-7 ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                <a
                  href={project.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${project.name} live application`}
                  className="group block overflow-hidden rounded-lg border border-border bg-card shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-secondary/30">
                    <img
                      src={project.image}
                      alt={project.imageAlt}
                      width={1440}
                      height={900}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.015]"
                    />
                  </div>
                </a>
              </div>

              <div className={`flex flex-col justify-center lg:col-span-5 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="font-mono text-xs text-primary">0{index + 1}</span>
                  <span className="h-px w-8 bg-border" />
                  <span className="text-xs uppercase text-muted-foreground">{project.category}</span>
                </div>
                <h2 className="text-4xl font-bold md:text-5xl">{project.name}</h2>
                <p className="mt-2 font-mono text-xs text-primary">{project.domain}</p>
                <p className="mt-6 leading-relaxed text-muted-foreground">{project.summary}</p>
                <p className="mt-4 leading-relaxed text-foreground/90">{project.outcome}</p>

                <ul className="mt-7 space-y-3">
                  {project.capabilities.map((capability) => (
                    <li key={capability} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span key={tag} className="border border-border bg-secondary/20 px-2.5 py-1 font-mono text-xs text-muted-foreground">
                      {tag}
                    </span>
                  ))}
                </div>

                <Button asChild className="mt-8 w-fit">
                  <a href={project.url} target="_blank" rel="noreferrer">
                    Explore live product <ArrowUpRight />
                  </a>
                </Button>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}